import { useEffect, useState } from "react";
import { BookMarked, ClipboardPaste, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Alert } from "@/components/ui/alert";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ApiError, submitTranscript } from "@/lib/api";
import { currentApiOrigin, loadBookmarkletCode, toBookmarkletHref } from "@/lib/bookmarklet";

interface TranscriptFallbackProps {
  videoId: string;
  /** Re-read the job summary so the page picks up a supplied transcript. */
  onRefresh: () => void;
}

/**
 * Shown when the server could not fetch a transcript because YouTube blocked
 * it. The fix runs in the user's own browser (their IP is not throttled): either
 * a one-click bookmarklet, or pasting the words in.
 */
export function TranscriptFallback({ videoId, onRefresh }: TranscriptFallbackProps) {
  const [href, setHref] = useState<string | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadBookmarkletCode(currentApiOrigin())
      .then((code) => {
        if (!cancelled) setHref(toBookmarkletHref(code));
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const submitPaste = async () => {
    if (pasteText.trim().length === 0) {
      setError("Paste the transcript text first.");
      return;
    }
    setSubmitting(true);
    setError(null);
    setNotice(null);
    try {
      await submitTranscript(videoId, { text: pasteText, source: "paste" });
      setNotice("Transcript received. Building your quiz…");
      setPasteText("");
      onRefresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not send the transcript. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">YouTube blocked our server — fetch the transcript yourself</CardTitle>
        <CardDescription>
          YouTube throttles transcript requests from our servers, but not from your browser. Pick either
          option below and the quiz will continue automatically.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <p className="flex items-center gap-2 text-sm font-medium">
            <BookMarked className="size-4" />
            Option 1 — one-click bookmarklet
          </p>
          <ol className="ml-5 list-decimal space-y-1 text-sm text-muted-foreground">
            <li>Drag the button below onto your bookmarks bar.</li>
            <li>Open this video on YouTube in the same browser.</li>
            <li>
              Click the bookmarklet. It sends the captions back here and the quiz keeps building.
            </li>
          </ol>
          {loadError ? (
            <p className="text-sm text-muted-foreground">
              Could not load the bookmarklet — use the manual paste option instead.
            </p>
          ) : href ? (
            <a
              href={href}
              draggable
              onClick={(e) => e.preventDefault()}
              className="inline-flex h-9 items-center gap-2 rounded-md border border-input bg-transparent px-4 text-sm font-medium shadow-sm hover:bg-accent"
            >
              <BookMarked className="size-4" />
              Get VidQuiz transcript
            </a>
          ) : (
            <span className="inline-flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
              Preparing bookmarklet…
            </span>
          )}
        </div>

        <div className="space-y-2">
          <p className="flex items-center gap-2 text-sm font-medium">
            <ClipboardPaste className="size-4" />
            Option 2 — paste the transcript
          </p>
          <p className="text-sm text-muted-foreground">
            On YouTube open the video, click <span className="font-medium">… More → Show transcript</span>,
            copy the text, and paste it here.
          </p>
          <Label htmlFor={`transcript-paste-${videoId}`} className="sr-only">
            Transcript text
          </Label>
          <Textarea
            id={`transcript-paste-${videoId}`}
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            placeholder="Paste the full transcript here…"
            className="min-h-[140px]"
          />
          <div className="flex items-center gap-2">
            <Button size="sm" disabled={submitting} onClick={() => void submitPaste()}>
              {submitting && <Loader2 className="animate-spin" />}
              Send transcript
            </Button>
            <Button size="sm" variant="ghost" onClick={onRefresh}>
              <RefreshCw />
              Refresh status
            </Button>
          </div>
          {notice && <Alert variant="success">{notice}</Alert>}
          {error && <Alert variant="destructive">{error}</Alert>}
        </div>
      </CardContent>
    </Card>
  );
}
